import { DatePipe } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { Component, OnDestroy } from '@angular/core';
import { FormGroup, FormBuilder, FormControl, AbstractControl, ValidationErrors } from '@angular/forms';
import { BusinessService } from '@app/business/business.service';
import { SessionStorageService } from '@app/core';
import { InfoService } from '@app/core/info/info.service';
import { TranslateService } from '@ngx-translate/core';
import { ListOfCertificates, HardwareCertificatesFilters, PaginationParams, SortingParams, CertificateStatus } from '../hardware-certificates.interface';

@Component({
  selector: 'cloud-business-hardware-certificates-reports',
  templateUrl: './business-hardware-certificates-reports.component.html',
  styleUrls: ['./business-hardware-certificates-reports.component.scss']
})
export class BusinessHardwareCertificatesReportsComponent implements OnDestroy {
  searchForm: FormGroup;
  certificateTable: boolean = false;
  certificateStatuses = [];
  typeOfCertificates = [];
  eik: string;
  showTable: boolean = false;
  enableExport: boolean = false;
  totalCount = 0;
  emptyMassage: string = '';

  hardwareCertificates: ListOfCertificates[] = [];

  currentFilters: HardwareCertificatesFilters;
  currentPagination: PaginationParams = { pageIndex: 0, pageSize: 10 };
  currentSorting: SortingParams = { active: '', direction: '' };

  searchCriteria: any;
  loading = false;

  get fromStartDateMax(): Date | null {
    return this.searchForm.get('toStartDate')?.value;
  }

  get toStartDateMin(): Date | null {
    return this.searchForm.get('fromStartDate')?.value;
  }

  get fromExpiryDateMax(): Date | null {
    return this.searchForm.get('toExpiryDate')?.value;
  }

  get toExpiryDateMin(): Date | null {
    return this.searchForm.get('fromExpiryDate')?.value;
  }

  constructor(
    private formBuilder: FormBuilder,
    private translateService: TranslateService,
    private sessionStorageService: SessionStorageService,
    public businessService: BusinessService,
    public info: InfoService,
  ) { }

  ngOnInit(): void {
    this.translateService.get([
      'certificates.serviceTypes.qesPersonal',
      'certificates.serviceTypes.qesProfessional',
      'certificates.serviceTypes.other',
      'certificates.certificateStatus.valid',
      'certificates.certificateStatus.expired',
      'certificates.certificateStatus.holded',
      'certificates.certificateStatus.revoked'
    ]).subscribe(translations => {
      this.typeOfCertificates = [
        { name: translations['certificates.serviceTypes.qesPersonal'], value: '1101' },
        { name: translations['certificates.serviceTypes.qesProfessional'], value: '1102' },
        {
          name: translations['certificates.serviceTypes.other'], value: ['1103', '1104', '1105', '1106', '1134', '1135', '1112', '1114', '1116', '1117',
            '1121', '1122', '1128', '1132', '1133', '1156', '1168', '1206', '1235']
        },

      ];

      this.certificateStatuses = [
        { name: translations['certificates.certificateStatus.valid'], value: CertificateStatus.VALID },
        { name: translations['certificates.certificateStatus.expired'], value: CertificateStatus.EXPIRED },
        { name: translations['certificates.certificateStatus.holded'], value: CertificateStatus.HOLDED },
        { name: translations['certificates.certificateStatus.revoked'], value: CertificateStatus.REVOKED }
      ];
    });
    this.buildForm()
    this.getCurrentLoggedInAccount()
    const navState = history.state;

    if (navState?.fromSubmitManagementForm) {
      this.restoreFormState();
      this.loadCertificates();
    }

    const isFromCertificateHistory = navState.fromCertificateHistory === true;
    if (isFromCertificateHistory) {
      this.restoreFormState();
    }

    if (navState?.fromManagePage) {
      this.restoreFormState();
    }
  }

  getCurrentLoggedInAccount() {
    this.eik = this.sessionStorageService.getItem("currentCompany").eik;
    if (this.searchForm) {
      this.searchForm.patchValue({ vat: this.eik });
    } else {
      console.error('searchForm is undefined');
    }
  }

  buildForm() {
    this.searchForm = this.formBuilder.group({
      vat: new FormControl('', [this.readOnlyValidator]),
      type: new FormControl(''),
      serialNumber: new FormControl(''),
      clientName: new FormControl(''),
      clientIdentification: new FormControl(''),
      status: new FormControl(''),
      fromStartDate: new FormControl(''),
      toStartDate: new FormControl(''),
      fromExpiryDate: new FormControl(''),
      toExpiryDate: new FormControl(''),
    });
  }
  clearField(field: string) {
    if (this.searchForm.get(field)) {
      this.searchForm.get(field)?.setValue(null);
    }
  }
  readOnlyValidator(control: AbstractControl): ValidationErrors | null {
    return control.value ? null : { readOnly: true };
  }
  submitSearchForm() {
    if (!this.searchForm.valid) {
      return;
    }

    const datePipe = new DatePipe('en-US');
    const formValues = this.searchForm.value;
    let searchCriteria: any = {};
    const allowedKeys = ['type', 'serialNumber', 'clientName', 'clientIdentification', 'status', 'fromStartDate', 'toStartDate', 'fromExpiryDate', 'toExpiryDate'];
    
    Object.keys(formValues).forEach((key) => {
      if (key === 'vat') {
        return;
      }

      // Prevent prototype pollution by validating property names
      if (allowedKeys.includes(key) && Object.prototype.hasOwnProperty.call(formValues, key) && formValues[key]) {
        if (['fromStartDate', 'toStartDate', 'fromExpiryDate', 'toExpiryDate'].includes(key)) {
          searchCriteria[key] = datePipe.transform(formValues[key], 'dd.MM.yyyy');
        } else {
          searchCriteria[key] = formValues[key];
        }
      }
    });

    this.businessService.getCertificateStatisticData(searchCriteria, this.eik).subscribe((res: any) => {
      if (Array.isArray(res.body?.data) && res.body.data.length > 0) {
        this.showTable = true;
        this.searchCriteria = res.body.data;
      } else {
        this.showTable = false;
        this.searchCriteria = [];
        this.emptyMassage = this.translateService.instant('certificates.emptyMassage');
      }
      this.searchCriteria = res.body.data;
      this.totalCount = res.body.size;
    });

  }
  resetFilters() {
    this.searchForm.reset();
    this.searchForm.patchValue({
      vat: this.eik,
      type: '',
      serialNumber: '',
      clientName: '',
      clientIdentification: '',
      status: '',
      fromStartDate: '',
      toStartDate: '',
      fromExpiryDate: '',
      toExpiryDate: ''
    });

    this.showTable = false;
    this.hardwareCertificates = [];
    this.currentPagination = { pageIndex: 0, pageSize: 10 };
    this.sessionStorageService.setItem('hardwareCertificatesReportFilters', null);
    this.sessionStorageService.setItem('hardwareCertificatesReportData', null);
  }
  exportExcel() {
    if (!this.hasData()) {
      this.info.error(this.translateService.instant('certificates.noDataExport'));
      return;
    } else {
      this.businessService.getCertificateStatisticDataInExcelFormat(this.searchForm.value, this.eik).subscribe((response) => {
        const contentDisposition = response.headers.get('Content-Disposition');
        const matches = /filename="?([^";]+)"?/i.exec(contentDisposition);
        const fileName = (matches && matches[1]) ? matches[1].trim() : 'Portal Report.xlsx';
        const blob = new Blob([response.body], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        window.URL.revokeObjectURL(url);
        this.info.success(this.translateService.instant('certificates.exportSuccess'));
      });

    }
  }


  hasData(): boolean {
    return this.hardwareCertificates && Array.isArray(this.hardwareCertificates) && this.hardwareCertificates.length > 0;
  }

  onFormSubmit() {
    this.currentPagination = { ...this.currentPagination, pageIndex: 0 } // Reset pagination when form is submitted
    this.loadCertificates();
  }

  loadCertificates() {
    if (!this.searchForm.valid) {
      return;
    }
    const datePipe = new DatePipe('en-US');
    const formValues = { ...this.searchForm.value }; // new reference so the original form values are not mutated after the DatePipe
    const allowedKeys = ['type', 'serialNumber', 'clientName', 'clientIdentification', 'status', 'fromStartDate', 'toStartDate', 'fromExpiryDate', 'toExpiryDate'];

    delete formValues.vat; // Remove 'vat' from formValues as it is read-only

    Object.keys(formValues).forEach((key) => {
      if (key === 'vat') {
        return;
      }

      // Prevent prototype pollution by validating property names
      if (allowedKeys.includes(key) && Object.prototype.hasOwnProperty.call(formValues, key) && formValues[key]) {
        if (['fromStartDate', 'toStartDate', 'fromExpiryDate', 'toExpiryDate'].includes(key)) {
          formValues[key] = datePipe.transform(formValues[key], 'dd.MM.yyyy');
        } else {
          formValues[key] = formValues[key];
        }
      }
    });

    formValues.hasPaging = true; // This is a flag to indicate that pagination is enabled

    const params = this.buildHttpParams(formValues, this.currentPagination, this.currentSorting);

    this.loading = true;
    this.businessService.getCertificateStatisticData(params, this.eik).subscribe({
      next: (res) => {
        this.loading = false;
        const data = res.body?.data;
        const size = res.body?.size;
        this.updateHardwareCertificates(data, size);
      },
      error: (err) => {
        console.error('Error loading certificates:', err);
        this.showTable = false;
        this.loading = false;
      }
    })

  }

  private updateHardwareCertificates(data: any, size: any) {
    if (Array.isArray(data) && data.length > 0) {
      this.showTable = true;
      this.hardwareCertificates = data;
      this.totalCount = size;
      this.emptyMassage = '';
    } else {
      this.showTable = false;
      this.hardwareCertificates = [];
      this.emptyMassage = this.translateService.instant('certificates.emptyMassage');
    }
  }

  onPageChange(event: PaginationParams) {
    this.currentPagination = event;
    this.loadCertificates();
  }

  onSortChange(event: SortingParams) {
    this.currentSorting = event;
    this.loadCertificates();
  }

  private buildHttpParams(filters: HardwareCertificatesFilters, pagination: PaginationParams, sorting?: SortingParams): HttpParams {
    let params = new HttpParams();

    // Add pagination parameters
    params = params.set('offset', (pagination.pageIndex).toString());
    params = params.set('limit', pagination.pageSize.toString());

    // Add sorting parameters
    if (sorting && sorting.active && sorting.direction) {
      params = params.set('orderBy', sorting.active);
      params = params.set('order', sorting.direction.toUpperCase());
    }

    // Add filter parameters (only if they have values)
    if (filters) {
      Object.keys(filters).forEach(key => {
        // Prevent prototype pollution by validating property names
        if (Object.prototype.hasOwnProperty.call(filters, key)) {
          const value = filters[key];
          if (value !== undefined && value !== null && value !== '') {
            params = params.set(key, value.toString());
          }
        }
      });
    }

      return params;
    }

  private saveFormState(): void {
    const formValue = this.searchForm.value;
    const formState = {
      type: formValue.type,
      serialNumber: formValue.serialNumber,
      clientName: formValue.clientName,
      clientIdentification: formValue.clientIdentification,
      status: formValue.status,
      fromStartDate: formValue.fromStartDate,
      toStartDate: formValue.toStartDate,
      fromExpiryDate: formValue.fromExpiryDate,
      toExpiryDate: formValue.toExpiryDate,
    };
    this.sessionStorageService.setItem('hardwareCertificatesReportFilters', formState);
    this.sessionStorageService.setItem('hardwareCertificatesReportData', {data: this.hardwareCertificates, totalCount: this.totalCount});
  }

  private restoreFormState(): void {
    const savedFormState = this.sessionStorageService.getItem('hardwareCertificatesReportFilters');
    if (savedFormState) {
      this.searchForm.patchValue(savedFormState);
    }
    const savedReportData = this.sessionStorageService.getItem('hardwareCertificatesReportData');
    if (savedReportData) {
      this.hardwareCertificates = savedReportData.data;
      this.totalCount = savedReportData.totalCount;
      this.updateHardwareCertificates(this.hardwareCertificates, this.totalCount);
    }
  }

  ngOnDestroy(): void {
    this.saveFormState();
  }
}
